import { Link } from 'react-router-dom';
import { ContactLink } from '@/components/contact';

/**
 * Privacy Policy Page
 *
 * Comprehensive privacy policy for FlowIO compliant with GDPR and CCPA guidelines.
 * This page is publicly accessible without authentication.
 *
 * WP-CONTACT-1: Updated to use ContactLink instead of mailto
 */
export function PrivacyPolicyPage() {
  const effectiveDate = 'December 1, 2025';
  const lastUpdated = 'February 11, 2026';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-primary-600">
              FlowIO
            </Link>
            <nav className="flex gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <article className="bg-white rounded-lg shadow-sm p-8 sm:p-12">
          {/* Title */}
          <header className="mb-10 pb-8 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <div className="text-sm text-gray-500 space-y-1">
              <p>
                <span className="font-medium">Effective Date:</span> {effectiveDate}
              </p>
              <p>
                <span className="font-medium">Last Updated:</span> {lastUpdated}
              </p>
            </div>
          </header>

          {/* Table of Contents */}
          <nav className="mb-10 p-6 bg-gray-50 rounded-lg" aria-label="Table of contents">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Table of Contents
            </h2>
            <ol className="list-decimal list-inside space-y-2 text-sm text-primary-600">
              <li><a href="#introduction" className="hover:underline">Introduction</a></li>
              <li><a href="#information-we-collect" className="hover:underline">Information We Collect</a></li>
              <li><a href="#how-we-use-information" className="hover:underline">How We Use Your Information</a></li>
              <li><a href="#data-sharing" className="hover:underline">Data Sharing and Third Parties</a></li>
              <li><a href="#data-retention" className="hover:underline">Data Retention</a></li>
              <li><a href="#security" className="hover:underline">Security Measures</a></li>
              <li><a href="#your-rights" className="hover:underline">Your Rights and Choices</a></li>
              <li><a href="#cookies" className="hover:underline">Cookies and Tracking Technologies</a></li>
              <li><a href="#childrens-privacy" className="hover:underline">Children's Privacy</a></li>
              <li><a href="#international-users" className="hover:underline">International Users</a></li>
              <li><a href="#changes" className="hover:underline">Changes to This Policy</a></li>
              <li><a href="#contact" className="hover:underline">Contact Us</a></li>
            </ol>
          </nav>

          {/* Policy Sections */}
          <div className="prose prose-gray max-w-none">
            {/* Section 1: Introduction */}
            <section id="introduction" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-600 mb-4">
                Welcome to FlowIO. We are committed to protecting your privacy and ensuring
                the security of your personal and financial information. This Privacy Policy
                explains how FlowIO ("we," "our," or "us") collects, uses, discloses, and
                safeguards your information when you use our personal finance management
                application and related services (collectively, the "Service").
              </p>
              <p className="text-gray-600 mb-4">
                By accessing or using our Service, you acknowledge that you have read,
                understood, and agree to be bound by this Privacy Policy. If you do not
                agree with our policies and practices, please do not use our Service.
              </p>
              <p className="text-gray-600">
                This policy applies to information we collect through our website, mobile
                applications, and any other online features, services, or platforms we
                offer that link to this Privacy Policy.
              </p>
            </section>

            {/* Section 2: Information We Collect */}
            <section id="information-we-collect" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                2. Information We Collect
              </h2>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                2.1 Information You Provide Directly
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">Account Information:</span> When you create
                  an account, we collect your email address, password (stored securely using
                  industry-standard hashing), and optional profile information such as your
                  name.
                </li>
                <li>
                  <span className="font-medium">Communication Data:</span> When you contact
                  our support team or respond to surveys, we collect the information you
                  provide in those communications.
                </li>
                <li>
                  <span className="font-medium">Payment Information:</span> When you
                  subscribe to our paid services, payment processing is handled by Stripe.
                  We do not store your full credit card number; we only receive limited
                  information such as the last four digits and card type for your records.
                </li>
                <li>
                  <span className="font-medium">Phone Number:</span> If you enable SMS-based
                  multi-factor authentication, we collect your mobile phone number. Your
                  phone number is encrypted at rest and is used exclusively for sending
                  security verification codes. We never use your phone number for marketing
                  or share it with third parties except Twilio for SMS delivery.
                </li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                2.2 Financial Data from Connected Accounts
              </h3>
              <p className="text-gray-600 mb-4">
                With your explicit consent, we use Plaid Inc. to securely connect to your
                financial institutions. Through this connection, we collect:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Account information (account names, types, balances)</li>
                <li>Transaction history (date, merchant, amount, category)</li>
                <li>Financial institution information (bank name, routing numbers)</li>
                <li>Account holder information as provided by your financial institution</li>
              </ul>
              <p className="text-gray-600 mb-4">
                We do not store your financial institution login credentials. Plaid securely
                manages the connection between FlowIO and your bank. For more information
                about how Plaid handles your data, please review{' '}
                <a
                  href="https://plaid.com/legal/#end-user-privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  Plaid's End User Privacy Policy
                </a>.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                2.3 Information Collected Automatically
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">Device Information:</span> Browser type,
                  operating system, device identifiers, screen resolution, and device
                  fingerprint data used for fraud prevention and security.
                </li>
                <li>
                  <span className="font-medium">Usage Data:</span> Pages visited, features
                  used, click patterns, time spent on pages, and navigation paths.
                </li>
                <li>
                  <span className="font-medium">Log Data:</span> IP address, access times,
                  referring URLs, and error logs (sanitized to remove personal information).
                </li>
                <li>
                  <span className="font-medium">Location Data:</span> General geographic
                  location based on IP address (country/region level only).
                </li>
                <li>
                  <span className="font-medium">Trusted Device Tokens:</span> When you mark
                  a device as "trusted," we store a secure token linking your device to your
                  account to streamline future logins and detect unauthorized access attempts.
                </li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                2.4 Documents You Upload
              </h3>
              <p className="text-gray-600 mb-4">
                You may upload financial documents such as bank statements, CSV exports, or
                PDF files to import transaction data. When you upload documents, we collect:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>The document file itself (temporarily stored for processing)</li>
                <li>Financial institution name and account identifiers</li>
                <li>Transaction details extracted from the document</li>
                <li>Statement period dates and account balances</li>
                <li>Any personal information contained in the document (names, addresses)</li>
              </ul>
              <p className="text-gray-600 mb-4">
                Uploaded documents are encrypted using AES-256 encryption during processing.
                Original documents are automatically deleted within 30 days after successful
                import. Extracted transaction data is retained according to our standard data
                retention policy.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                2.5 Authentication Credentials
              </h3>
              <p className="text-gray-600 mb-4">
                We offer multiple authentication methods for your security:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">Passkeys/WebAuthn:</span> If you register a
                  passkey for passwordless login, we store a cryptographic credential ID and
                  public key associated with your device. <strong>No biometric data (fingerprint,
                  face scan) is ever transmitted to or stored on our servers.</strong> Biometric
                  verification happens entirely on your device.
                </li>
                <li>
                  <span className="font-medium">TOTP Authenticator Apps:</span> If you enable
                  authenticator-based MFA, we store an encrypted seed for generating time-based
                  codes.
                </li>
                <li>
                  <span className="font-medium">Magic Links:</span> If you use email-based
                  passwordless login, we generate temporary secure tokens sent to your email.
                  These tokens expire after 15 minutes.
                </li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                2.6 Data from Other Financial Services
              </h3>
              <p className="text-gray-600 mb-4">
                You may import data from other personal finance applications including:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Mint (Intuit) export files</li>
                <li>YNAB (You Need A Budget) export files</li>
                <li>Quicken export files</li>
                <li>Other CSV or OFX format exports</li>
              </ul>
              <p className="text-gray-600 mb-4">
                When you import data from these services, we collect the transaction history,
                category assignments, and any account information contained in the export files.
                This data is processed and stored according to our standard data handling practices.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                2.7 Mobile Application Data
              </h3>
              <p className="text-gray-600 mb-4">
                When you use our mobile applications (iOS or Android), we may additionally collect:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">Device Identifiers:</span> Anonymized device
                  identifiers for analytics and crash reporting.
                </li>
                <li>
                  <span className="font-medium">Push Notification Tokens:</span> If you enable
                  push notifications, we store tokens provided by Apple (APNs) or Google (FCM)
                  to deliver notifications to your device.
                </li>
                <li>
                  <span className="font-medium">Biometric Capability:</span> Whether your device
                  supports Face ID, Touch ID, or fingerprint authentication (we do not receive
                  the biometric data itself).
                </li>
                <li>
                  <span className="font-medium">Secure Storage:</span> Authentication tokens are
                  stored in your device's secure enclave (iOS Keychain / Android Keystore) and
                  are not accessible to other applications.
                </li>
              </ul>
            </section>

            {/* Section 3: How We Use Information */}
            <section id="how-we-use-information" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-gray-600 mb-4">
                We use the information we collect for the following purposes:
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                3.1 Service Delivery
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Provide, maintain, and improve our Service</li>
                <li>Aggregate and categorize your financial transactions</li>
                <li>Generate personalized financial visualizations and insights</li>
                <li>Process your subscription payments</li>
                <li>Respond to your inquiries and support requests</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                3.2 Communication
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Send transactional emails (account verification, password resets, payment receipts)</li>
                <li>Deliver service-related notifications and updates</li>
                <li>Send promotional communications (only with your consent; you may opt out at any time)</li>
                <li>Send SMS verification codes for multi-factor authentication (only if you opt in to SMS MFA)</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                3.3 SMS Communications (TCPA Compliance)
              </h3>
              <p className="text-gray-600 mb-4">
                If you enable SMS-based multi-factor authentication, you consent to receive
                automated text messages containing security verification codes. By providing
                your phone number and enabling SMS MFA, you agree that:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>You are the owner or authorized user of the phone number provided</li>
                <li>You consent to receive automated SMS messages for authentication purposes only</li>
                <li>Standard message and data rates from your mobile carrier may apply</li>
                <li>Message frequency varies based on your login activity</li>
                <li>You can disable SMS MFA at any time in your account settings</li>
              </ul>
              <p className="text-gray-600 mb-4">
                We will never send marketing or promotional messages via SMS. SMS is used
                exclusively for security verification codes. To stop receiving SMS messages,
                disable SMS MFA in your security settings or reply STOP to any message.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                3.4 Security and Compliance
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Detect, prevent, and address fraud, security threats, and technical issues</li>
                <li>Verify your identity and prevent unauthorized access</li>
                <li>Comply with legal obligations and regulatory requirements</li>
                <li>Enforce our Terms of Service and other policies</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                3.5 Analytics and Improvement
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Analyze usage patterns to improve user experience</li>
                <li>Develop new features and functionality</li>
                <li>Conduct research and analysis on aggregated, anonymized data</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                3.6 Automated Processing and Decision-Making
              </h3>
              <p className="text-gray-600 mb-4">
                We use automated systems to process your financial data. This includes:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">Transaction Categorization:</span> We use
                  machine learning algorithms to automatically categorize your transactions
                  (e.g., identifying a transaction as "Groceries" or "Utilities"). You can
                  manually override any automatic categorization.
                </li>
                <li>
                  <span className="font-medium">Document Classification:</span> When you
                  upload financial documents, we use automated systems to identify the
                  document type (bank statement, credit card statement, etc.) and extract
                  relevant data.
                </li>
                <li>
                  <span className="font-medium">Transfer Detection:</span> We automatically
                  identify transactions that appear to be transfers between your own accounts
                  to avoid double-counting in your financial overview.
                </li>
                <li>
                  <span className="font-medium">Fraud Detection:</span> We use automated
                  systems to detect potentially suspicious activity on your account for
                  security purposes.
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                <strong>Your Rights Regarding Automated Processing (GDPR Article 22):</strong>{' '}
                If you are in the European Economic Area, you have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Request human review of any automated categorization or classification</li>
                <li>Express your point of view regarding automated decisions</li>
                <li>Contest decisions made through automated processing</li>
              </ul>
              <p className="text-gray-600">
                To request human review or contest an automated decision,{' '}
                <ContactLink
                  topic="privacy"
                  context="PrivacyPolicyPage - GDPR automated processing review"
                  className="text-primary-600 hover:underline cursor-pointer"
                >
                  contact our privacy team
                </ContactLink>.
              </p>
            </section>

            {/* Section 4: Data Sharing */}
            <section id="data-sharing" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                4. Data Sharing and Third Parties
              </h2>
              <p className="text-gray-600 mb-4">
                We do not sell your personal information. We share your information only
                in the following circumstances:
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                4.1 Service Providers
              </h3>
              <p className="text-gray-600 mb-4">
                We share information with trusted third-party service providers who assist
                us in operating our Service:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">Plaid Inc.:</span> Financial data aggregation.
                  Plaid connects to your financial institutions to retrieve your account
                  and transaction data. For more information, see{' '}
                  <a
                    href="https://plaid.com/legal/#end-user-privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Plaid's End User Privacy Policy
                  </a>.
                </li>
                <li>
                  <span className="font-medium">Akoya:</span> Alternative financial data
                  aggregation. Depending on your financial institution's preference, we may
                  use Akoya instead of Plaid to connect to your accounts. Akoya provides
                  direct API connections to participating banks. For more information, see{' '}
                  <a
                    href="https://www.akoya.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Akoya's Privacy Policy
                  </a>.
                </li>
                <li>
                  <span className="font-medium">Stripe Inc.:</span> Payment processing.
                  Stripe handles subscription billing and payment card processing. For more
                  information, see{' '}
                  <a
                    href="https://stripe.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Stripe's Privacy Policy
                  </a>.
                </li>
                <li>
                  <span className="font-medium">Postmark (ActiveCampaign):</span> Email
                  delivery for transactional and notification emails.
                </li>
                <li>
                  <span className="font-medium">Twilio Inc.:</span> SMS delivery for
                  multi-factor authentication codes. Phone numbers are used solely for
                  security verification and are never used for marketing purposes.
                </li>
                <li>
                  <span className="font-medium">Linode (Akamai):</span> Cloud infrastructure
                  hosting our servers in the United States.
                </li>
                <li>
                  <span className="font-medium">Neon:</span> PostgreSQL database hosting
                  for secure data storage.
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                These service providers are contractually obligated to use your information
                only for the purposes of providing services to us and must maintain
                appropriate security measures.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                4.2 Legal Requirements
              </h3>
              <p className="text-gray-600 mb-4">
                We may disclose your information if required by law, such as in response to:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>A subpoena, court order, or other legal process</li>
                <li>A request from law enforcement or government authorities</li>
                <li>Protection of our rights, privacy, safety, or property</li>
                <li>Prevention of fraud or other illegal activities</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                4.3 Business Transfers
              </h3>
              <p className="text-gray-600 mb-4">
                If FlowIO is involved in a merger, acquisition, or sale of assets, your
                information may be transferred as part of that transaction. We will notify
                you of any such change and any choices you may have regarding your
                information.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                4.4 With Your Consent
              </h3>
              <p className="text-gray-600">
                We may share your information with other parties when you give us explicit
                consent to do so.
              </p>
            </section>

            {/* Section 5: Data Retention */}
            <section id="data-retention" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. Data Retention
              </h2>
              <p className="text-gray-600 mb-4">
                We retain your information for as long as necessary to provide our Service
                and fulfill the purposes described in this Privacy Policy:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">Account Data:</span> Retained while your
                  account is active and for up to 30 days after deletion request to
                  facilitate account recovery.
                </li>
                <li>
                  <span className="font-medium">Financial Data:</span> Transaction data
                  is retained while your account is active. Upon account deletion, financial
                  data is permanently deleted within 30 days.
                </li>
                <li>
                  <span className="font-medium">Usage Data:</span> Aggregated, anonymized
                  usage data may be retained indefinitely for analytical purposes.
                </li>
                <li>
                  <span className="font-medium">Legal Requirements:</span> Some data may
                  be retained longer if required by law or for legitimate business purposes
                  (e.g., fraud prevention, dispute resolution).
                </li>
              </ul>
            </section>

            {/* Section 6: Security */}
            <section id="security" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                6. Security Measures
              </h2>
              <p className="text-gray-600 mb-4">
                We implement comprehensive security measures to protect your information:
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                6.1 Encryption
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>
                  <span className="font-medium">In Transit:</span> All data transmitted
                  between your device and our servers is encrypted using TLS 1.3 (HTTPS).
                </li>
                <li>
                  <span className="font-medium">At Rest:</span> Sensitive data, including
                  financial access tokens, is encrypted using AES-256 encryption.
                </li>
                <li>
                  <span className="font-medium">Passwords:</span> Stored using Argon2
                  hashing algorithm with unique salts.
                </li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                6.2 Access Controls
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Multi-factor authentication (MFA) available for all accounts</li>
                <li>Role-based access controls for our internal systems</li>
                <li>Regular security audits and penetration testing</li>
                <li>Employee access limited on a need-to-know basis</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                6.3 Infrastructure Security
              </h3>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Servers hosted in secure, SOC 2 compliant data centers</li>
                <li>Regular security patches and updates</li>
                <li>Intrusion detection and monitoring systems</li>
                <li>Regular automated backups with encryption</li>
              </ul>

              <p className="text-gray-600">
                While we strive to use commercially acceptable means to protect your
                information, no method of transmission over the Internet or electronic
                storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            {/* Section 7: Your Rights */}
            <section id="your-rights" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                7. Your Rights and Choices
              </h2>
              <p className="text-gray-600 mb-4">
                Depending on your location, you may have certain rights regarding your
                personal information:
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.1 Access and Portability
              </h3>
              <p className="text-gray-600 mb-4">
                You have the right to request a copy of the personal information we hold
                about you. You can export your data in a machine-readable format from your
                account settings.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.2 Correction
              </h3>
              <p className="text-gray-600 mb-4">
                You have the right to request that we correct inaccurate personal
                information. You can update most information directly through your account
                settings.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.3 Deletion
              </h3>
              <p className="text-gray-600 mb-4">
                You have the right to request deletion of your personal information. You
                can delete your account from the settings page, or contact us to request
                deletion. We will process deletion requests within 30 days, subject to
                legal retention requirements.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.4 Opt-Out of Marketing
              </h3>
              <p className="text-gray-600 mb-4">
                You can opt out of marketing communications at any time by clicking the
                "unsubscribe" link in any marketing email or updating your communication
                preferences in your account settings.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.5 Disconnect Financial Accounts
              </h3>
              <p className="text-gray-600 mb-4">
                You can disconnect linked financial accounts at any time through your
                account settings. This will stop the sync of new data, and you can request
                deletion of previously imported data.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.6 GDPR Rights (European Users)
              </h3>
              <p className="text-gray-600 mb-4">
                If you are in the European Economic Area (EEA), you have additional rights
                under the General Data Protection Regulation (GDPR), including:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Right to restrict processing of your personal data</li>
                <li>Right to object to processing based on legitimate interests</li>
                <li>Right to withdraw consent at any time</li>
                <li>Right to lodge a complaint with your local supervisory authority</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                7.7 CCPA Rights (California Residents)
              </h3>
              <p className="text-gray-600 mb-4">
                If you are a California resident, you have rights under the California
                Consumer Privacy Act (CCPA), including:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether personal information is sold or disclosed</li>
                <li>Right to say no to the sale of personal information (we do not sell your data)</li>
                <li>Right to equal service and price (no discrimination for exercising rights)</li>
              </ul>
              <p className="text-gray-600">
                To exercise any of these rights, please{' '}
                <ContactLink
                  topic="privacy"
                  context="PrivacyPolicyPage - CCPA rights request"
                  className="text-primary-600 hover:underline cursor-pointer"
                >
                  contact our privacy team
                </ContactLink>.
              </p>
            </section>

            {/* Section 8: Cookies */}
            <section id="cookies" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-600 mb-4">
                We use cookies and similar technologies to provide and improve our Service:
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                8.1 Essential Cookies
              </h3>
              <p className="text-gray-600 mb-4">
                Required for the Service to function properly. These cookies manage your
                session, remember your login status, and maintain security. These cannot
                be disabled.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                8.2 Functional Cookies
              </h3>
              <p className="text-gray-600 mb-4">
                Remember your preferences (such as language or display settings) to
                provide a more personalized experience.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                8.3 Analytics Cookies
              </h3>
              <p className="text-gray-600 mb-4">
                Help us understand how users interact with our Service. This data is
                aggregated and anonymized. You can opt out of analytics cookies in your
                browser settings or through our cookie preferences.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-3">
                8.4 Managing Cookies
              </h3>
              <p className="text-gray-600">
                Most web browsers allow you to control cookies through their settings.
                Note that disabling certain cookies may limit the functionality of our
                Service. For more information about cookies and how to manage them, visit{' '}
                <a
                  href="https://www.allaboutcookies.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  www.allaboutcookies.org
                </a>.
              </p>
            </section>

            {/* Section 9: Children's Privacy */}
            <section id="childrens-privacy" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                9. Children's Privacy
              </h2>
              <p className="text-gray-600 mb-4">
                Our Service is not intended for individuals under the age of 18. We do
                not knowingly collect personal information from children under 18. If you
                are a parent or guardian and believe your child has provided us with
                personal information, please{' '}
                <ContactLink
                  topic="privacy"
                  context="PrivacyPolicyPage - Children's privacy concern"
                  className="text-primary-600 hover:underline cursor-pointer"
                >
                  contact our privacy team
                </ContactLink>
                . We will take steps to delete such information from our systems.
              </p>
              <p className="text-gray-600">
                If we discover that we have collected personal information from a child
                under 18, we will promptly delete that information.
              </p>
            </section>

            {/* Section 10: International Users */}
            <section id="international-users" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                10. International Users
              </h2>
              <p className="text-gray-600 mb-4">
                FlowIO is operated from the United States. If you are accessing our
                Service from outside the United States, please be aware that your
                information will be transferred to, stored, and processed in the United
                States where our servers are located.
              </p>
              <p className="text-gray-600 mb-4">
                By using our Service, you consent to the transfer of your information to
                the United States. We take appropriate measures to ensure your data is
                treated securely and in accordance with this Privacy Policy, regardless
                of where it is processed.
              </p>
              <p className="text-gray-600">
                For users in the European Economic Area (EEA), we rely on Standard
                Contractual Clauses approved by the European Commission as a legal basis
                for data transfers to the United States.
              </p>
            </section>

            {/* Section 11: Changes */}
            <section id="changes" className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                11. Changes to This Policy
              </h2>
              <p className="text-gray-600 mb-4">
                We may update this Privacy Policy from time to time to reflect changes
                in our practices, technology, legal requirements, or other factors. When
                we make material changes, we will:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Update the "Last Updated" date at the top of this policy</li>
                <li>Notify you by email (for registered users) or through a prominent notice on our Service</li>
                <li>Where required by law, obtain your consent before implementing material changes</li>
              </ul>
              <p className="text-gray-600">
                We encourage you to review this Privacy Policy periodically to stay
                informed about how we protect your information.
              </p>
            </section>

            {/* Section 12: Contact */}
            <section id="contact" className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                12. Contact Us
              </h2>
              <p className="text-gray-600 mb-4">
                If you have any questions, concerns, or requests regarding this Privacy
                Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-medium text-gray-800 mb-3">
                  General Privacy Inquiries
                </h3>
                <p className="text-gray-700 mb-4">
                  <ContactLink
                    topic="privacy"
                    context="PrivacyPolicyPage - General privacy inquiry"
                    className="text-primary-600 hover:underline cursor-pointer"
                  >
                    Contact our privacy team
                  </ContactLink>
                </p>
                <p className="text-gray-600 text-sm">
                  We will respond to your inquiry within 30 days. For requests related
                  to your personal data rights, we may need to verify your identity before
                  processing your request.
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-800 mb-3">
                  Data Protection Officer (GDPR)
                </h3>
                <p className="text-gray-600 mb-4">
                  For users in the European Economic Area (EEA), you may contact our
                  Data Protection Officer for questions about how we handle your personal
                  data or to exercise your rights under GDPR:
                </p>
                <p className="text-gray-700 mb-4">
                  <ContactLink
                    topic="dpo"
                    context="PrivacyPolicyPage - GDPR DPO inquiry"
                    className="text-primary-600 hover:underline cursor-pointer"
                  >
                    Contact our Data Protection Officer
                  </ContactLink>
                </p>
                <p className="text-gray-600 text-sm">
                  Our DPO will respond to GDPR-related inquiries within 30 days as required
                  by regulation. For complaints that cannot be resolved, you have the right
                  to lodge a complaint with your local data protection supervisory authority.
                </p>
              </div>
            </section>
          </div>

          {/* Footer Note */}
          <footer className="mt-10 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              This Privacy Policy was last updated on {lastUpdated}. By continuing to use
              FlowIO, you acknowledge that you have read and understood this policy.
            </p>
          </footer>
        </article>

        {/* Related Links */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <Link to="/terms" className="hover:text-gray-700 hover:underline">
            Terms of Service
          </Link>
          <span className="mx-3">|</span>
          <Link to="/security" className="hover:text-gray-700 hover:underline">
            Security
          </Link>
          <span className="mx-3">|</span>
          <Link to="/" className="hover:text-gray-700 hover:underline">
            Back to Home
          </Link>
        </div>
      </main>

      {/* Page Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            Copyright {new Date().getFullYear()} FlowIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicyPage;
